import { Card, BlockStack, InlineStack, Text, ProgressBar } from "@shopify/polaris";
import { useTranslation } from "../hooks/useTranslation";

interface QuotaDisplayProps {
  totalUsed: number;
  quotaLimit: number;
  submissionsRemaining: number;
  isTrial: boolean;
  billingCycleEnd: string | null;
}

export function QuotaDisplay({
  totalUsed,
  quotaLimit,
  submissionsRemaining,
  isTrial,
  billingCycleEnd,
}: QuotaDisplayProps) {
  const { t } = useTranslation();
  // Calculate usage percentage
  const usagePercentage = quotaLimit > 0 ? (totalUsed / quotaLimit) * 100 : 0;

  // Determine progress bar tone based on remaining percentage
  const remainingPercentage = 100 - usagePercentage;
  let progressTone: "success" | "attention" | "critical" | "info" = "success";

  if (isTrial) {
    progressTone = "info";
  } else if (remainingPercentage < 25) {
    progressTone = "critical";
  } else if (remainingPercentage < 50) {
    progressTone = "attention";
  }

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {isTrial
            ? t("dashboard.quota.title.trial")
            : t("dashboard.quota.title.subscription")}
        </Text>

        {/* Main usage display + remaining */}
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingLg" as="p">
              {totalUsed} / {quotaLimit}
            </Text>
            {!isTrial && (
              <Text variant="bodyMd" as="p" tone="subdued">
                {t("dashboard.quota.period.monthly")}
              </Text>
            )}
          </InlineStack>

          <ProgressBar
            progress={usagePercentage}
            tone={progressTone}
            size="medium"
          />

          <BlockStack gap="100">
          <Text variant="bodyMd" as="p">
            {submissionsRemaining > 0 ? (
              <>
                {submissionsRemaining === 1
                  ? t("dashboard.quota.remainingSingular", { count: submissionsRemaining })
                  : t("dashboard.quota.remaining", { count: submissionsRemaining })}
              </>
            ) : (
              <Text as="span" tone="critical" fontWeight="semibold">
                {t("dashboard.quota.noRemaining")}
              </Text>
            )}
          </Text>

          {/* Reset date for non-trial */}
          {!isTrial && billingCycleEnd && (
            <Text variant="bodySm" as="p" tone="subdued">
              {t("dashboard.quota.resetsOn", { date: formatDate(billingCycleEnd) })}
            </Text>
          )}

          {/* Trial info */}
          {isTrial && (
            <Text variant="bodySm" as="p" tone="subdued">
              {t("dashboard.quota.trialInfo", { limit: quotaLimit })}
            </Text>
          )}
        </BlockStack>
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
